// src/hooks/useNotificationSetup.ts
//
// Sets up Capacitor PushNotifications listeners for two events:
//  1. `registration`                  — FCM token ready → POST to /api/push/device-tokens
//  2. `pushNotificationActionPerformed` — user tapped a notification → navigate to action_url
//  3. `registrationError`              — log to console
//
// Does NOT call requestPermissions() / register() — that is the Settings
// toggle's job (Task 1.10). This hook only attaches reactive listeners.
//
// Cleanup: every addListener() Promise is collected and resolved on unmount
// so the handles are properly removed. (Capacitor addListener is *additive*,
// not a replacement — failing to clean up causes double-fires on each re-mount.)
//
// No-op on non-native platforms and when no workspace is loaded yet.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useWorkspaceStore } from '@/stores/workspace';
import { toast } from '@/lib/toast';

const DEVICE_LABEL_STORAGE_KEY = 'spent_tracker_device_label';

function getDeviceLabel(): string {
  try {
    const cached = localStorage.getItem(DEVICE_LABEL_STORAGE_KEY);
    if (cached) return cached;
    const ua = navigator.userAgent || '';
    // Android UA shape: "Mozilla/5.0 (Linux; Android 14; Pixel 7) ..."
    const m = ua.match(/Linux;\s*Android\s+\d+(?:\.\d+)*;\s*([^)]+?)(?:[);])/);
    const label = m ? m[1].trim() : 'Phone';
    localStorage.setItem(DEVICE_LABEL_STORAGE_KEY, label);
    return label;
  } catch {
    return 'Phone';
  }
}

async function registerToken(token: string, workspaceId: string): Promise<void> {
  try {
    await fetch('/api/push/device-tokens', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspaceId,
      },
      body: JSON.stringify({
        token,
        platform: 'android',
        label: getDeviceLabel(),
      }),
    });
  } catch (e) {
    console.error('[push] register token failed', e);
  }
}

/**
 * Mount once near the route root (next to useHardwareBack / useAutoUpdate).
 * Listens for FCM token registrations + tap-on-notification events.
 *
 * Does NOT call requestPermissions() / register() — that's the Settings
 * toggle's job. This hook only sets up reactive listeners.
 *
 * No-op on non-native platforms and when workspace not yet loaded.
 */
export function useNotificationSetup(): void {
  const navigate = useNavigate();
  const ws = useWorkspaceStore((s) => s.current);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!ws?.id) return;

    // Create the OS-level notification channel. AndroidManifest's
    // `default_notification_channel_id` meta-data is only a HINT to the
    // Firebase Messaging library — it does NOT actually create the channel
    // on the device. Capacitor's PushNotifications plugin also doesn't
    // auto-create channels. Without an explicit createChannel call, our
    // messages reach FCM with `channel_id: 'spent_tracker_default'` but
    // Android can't find such a channel on the device and silently drops
    // the system tray notification — server logs status='sent', user
    // sees nothing on lock screen.
    //
    // Importance 4 (HIGH) → heads-up notification + sound + vibrate.
    // Idempotent: calling createChannel for an existing id is a no-op on
    // Android 8+, so safe to invoke on every mount.
    //
    // Verbose logging added 2026-05-31 after user reported "Vickie 的手机
    // 有提示我的没有" — listChannels before/after lets us actually see in
    // tail / chrome devtools whether the channel ended up on the device.
    void (async () => {
      try {
        const before = await PushNotifications.listChannels();
        console.log('[push] channels before create:', JSON.stringify(before));
        await PushNotifications.createChannel({
          id: 'spent_tracker_default',
          name: 'AI 提醒',
          description: 'Receipt alerts, daily briefings, savings reminders',
          importance: 4,
          visibility: 1,
          lights: true,
          vibration: true,
        });
        const after = await PushNotifications.listChannels();
        console.log('[push] channels after create:', JSON.stringify(after));
      } catch (err) {
        console.warn('[push] createChannel failed', err);
      }
    })();

    const handles: Promise<{ remove: () => Promise<void> }>[] = [];

    handles.push(
      PushNotifications.addListener('registration', (t) => {
        void registerToken(t.value, ws.id);
      }),
    );

    handles.push(
      PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
        const url = event.notification.data?.action_url;
        if (typeof url === 'string' && url.startsWith('/')) {
          navigate(url);
        }
      }),
    );

    // Foreground receive: when a push arrives while the app is active,
    // Capacitor fires `pushNotificationReceived` and the OS does NOT display
    // anything in the system tray (this is by design — Capacitor leaves
    // in-app presentation to the app dev). Without this handler, tapping
    // "测试推送" while you're in Settings yields silence: server logs
    // status='sent' but the user sees nothing. Show a toast so foreground
    // pushes are at least visible. Added 2026-05-30 after user feedback
    // "手机没有收到 android 的提醒" — the FCM message was actually queued
    // (notification_log shows fcm_message_id) but the app was foregrounded.
    handles.push(
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        const title = notification.title ?? '';
        const body = notification.body ?? '';
        const displayText = [title, body].filter(Boolean).join(' · ');
        if (displayText) toast.info(displayText);
      }),
    );

    handles.push(
      PushNotifications.addListener('registrationError', (err) => {
        console.error('[push] registration error', err);
      }),
    );

    return () => {
      handles.forEach((h) => {
        h.then((handle) => handle.remove()).catch(() => {});
      });
    };
  }, [navigate, ws?.id]);
}
